/**
 * RhParametresClôturePanel — réglages RH de la commune.
 *
 * Deux blocs distincts, volontairement séparés :
 *   · **Congés** — qui valide les demandes (migration 196) ;
 *   · **Clôture de la paie** — verrouillage mensuel (mockup_rh_paie_cloture).
 *
 * Ils vivent dans la même table (`rh_parametres`) mais ne règlent pas la même
 * chose : les fondre ferait chercher le mode de validation des congés dans les
 * réglages de clôture de paie.
 */
import { useState, useEffect } from 'react';
import { Card, Alert, Badge } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import type { RhClotureParams } from '@roronoa-collectivites/shared-types';

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} aria-pressed={on}
      className={`relative h-6 w-11 rounded-full transition ${on ? 'bg-rh' : 'bg-surface2 border border-border-hi'}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${on ? 'right-0.5' : 'left-0.5'}`} />
    </button>
  );
}

export function RhParametresCloturePanel() {
  const [params, setParams] = useState<RhClotureParams | null>(null);
  const [modeConges, setModeConges] = useState<'manager' | 'rh_maire' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.rh.clotureParams().then(setParams).catch((e) => setError(e instanceof ApiException ? e.message : 'Erreur'));
    api.rh.modeValidationConges().then((r) => setModeConges(r.mode)).catch(() => { /* best-effort */ });
  }, []);

  async function patchModeConges(mode: 'manager' | 'rh_maire') {
    setError(null); setMsg(null);
    try {
      const r = await api.rh.setModeValidationConges(mode);
      setModeConges(r.mode); setMsg('Paramètres enregistrés.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function patch(p: Partial<RhClotureParams>) {
    if (!params) return;
    setError(null); setMsg(null);
    try {
      const next = await api.rh.updateClotureParams(p);
      setParams(next); setMsg('Paramètres enregistrés.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  if (!params) return <p className="text-sm text-ink-muted">Chargement…</p>;

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card className="p-5">
        <h2 className="mb-1 font-display text-base font-semibold text-ink">Congés</h2>
        <p className="mb-4 text-sm text-ink-muted">Qui prend la décision finale sur les demandes de congés de vos agents.</p>

        <Row
          title="Validation des congés"
          desc={
            modeConges === 'rh_maire'
              ? 'Les managers donnent leur avis, mais ne valident pas : les demandes restent en attente de votre décision.'
              : 'Chaque manager valide les congés de ses agents. S’il a un doute, il laisse la demande en attente : elle vous remonte.'
          }
          last
        >
          <select
            value={modeConges ?? 'manager'}
            onChange={(e) => void patchModeConges(e.target.value as 'manager' | 'rh_maire')}
            disabled={modeConges === null}
            className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink"
          >
            <option value="manager">Le manager valide</option>
            <option value="rh_maire">Le maire / la RH valide</option>
          </select>
        </Row>
      </Card>

      <Card className="p-5">
        <h2 className="mb-1 font-display text-base font-semibold text-ink">Clôture de la paie</h2>
        <p className="mb-4 text-sm text-ink-muted">Réglages du verrouillage mensuel. La date est calculée automatiquement pour laisser toujours assez de marge à la paie.</p>

        <Row title="Verrouillage automatique" desc="Gèle les plannings du mois à la date calculée. Recommandé.">
          <Toggle on={params.verrouAuto} onToggle={() => void patch({ verrouAuto: !params.verrouAuto })} />
        </Row>
        <Row title="Marge avant fin de mois" desc="Nombre minimum de jours ouvrés laissés avant le dernier jour du mois.">
          <div className="flex items-center gap-2">
            <input type="number" min={1} max={10} value={params.margeJoursOuvres}
              onChange={(e) => void patch({ margeJoursOuvres: Math.max(1, Math.min(10, Number(e.target.value) || 2)) })}
              className="h-10 w-20 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            <span className="text-sm text-ink-muted">jours ouvrés</span>
          </div>
        </Row>
        <Row title="Pré-validation automatique" desc="À la date, le mois est pré-validé automatiquement ; la RH n'a plus qu'à vérifier et confirmer.">
          <Toggle on={params.prevalidationAuto} onToggle={() => void patch({ prevalidationAuto: !params.prevalidationAuto })} />
        </Row>
        <Row title="Exception arrêts maladie / AT" desc="Autoriser l'ajout rétroactif d'arrêts après clôture (manager RH / maire uniquement).">
          <Toggle on={params.exceptionMaladie} onToggle={() => void patch({ exceptionMaladie: !params.exceptionMaladie })} />
        </Row>
        <Row title="Report automatique des modifs tardives" desc="Une modif sur un mois clôturé est reportée en régularisation sur le mois suivant, avec alerte.">
          <Toggle on={params.reportAuto} onToggle={() => void patch({ reportAuto: !params.reportAuto })} />
        </Row>
        <Row title="Réouverture exceptionnelle" desc="Qui peut rouvrir un mois clôturé (motif obligatoire, tracé)." last>
          <select value={params.reouvertureQui} onChange={(e) => void patch({ reouvertureQui: e.target.value as RhClotureParams['reouvertureQui'] })}
            className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
            <option value="maire_rh">Maire + manager RH</option>
            <option value="maire">Maire uniquement</option>
          </select>
        </Row>
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2">
          <Badge variant="rh">Info</Badge>
          <p className="text-sm text-ink-muted">La date exacte de verrouillage est calculée chaque mois (3ᵉ jour ouvré avant la fin du mois, week-ends et fériés gérés) et affichée dans l'onglet « Paie &amp; clôture ».</p>
        </div>
      </Card>
    </div>
  );
}

function Row({ title, desc, children, last }: { title: string; desc: string; children: React.ReactNode; last?: boolean }) {
  return (
    <div className={`flex items-center gap-3 py-3 ${last ? '' : 'border-b border-border'}`}>
      <div className="flex-1">
        <div className="text-sm font-medium text-ink">{title}</div>
        <div className="text-xs text-ink-subtle">{desc}</div>
      </div>
      {children}
    </div>
  );
}
